import { Component, Input, OnChanges, SimpleChanges } from "@angular/core";
import { VTableRowStatus } from "@app/directives/_layout/v-table/_enums/VTableRowStatus.enum";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { IAggregateFilterEvent, IFilterBarTab } from "@app/directives/_resourceFilters/filter-bar/filter-bar.models";
import { FilterBarService } from "@app/directives/_resourceFilters/filter-bar/filter-bar.service";
import { VPillsComponent } from "@app/directives/_veri/v-pills/v-pills.component";
import { SortDirection } from "@app/enum/SortDirection";
import { VehicleType } from "@app/enum/VehicleType.enum";
import { FtCloudConfigExceptionError, StreamaxRecordOverwrite } from "@app/main/modules/foundation/_enums/camera-configs/CameraConfigEnums";
import { IAccountGroupConfigDto, IChannelSpecificSettings, IPeripheralWithConfigRecord, IProfileExceptionErrors } from "@app/main/modules/foundation/_models/_camera-configs/CameraConfigModels";
import { ICameraDeviceParameterProfileSettings } from "@app/services/camera/streamax-camera-profile-service";
import { TranslationService } from "@app/services/translation.service";
import { ITimeZoneInfo } from "@app/userPreferences/model/ITimeZoneInfo";
import './streamax-configs-device-list.component.scss';

@Component({
    selector: 'streamax-configs-device-list',
    template: require('./streamax-configs-device-list.component.html')
})
export class StreamaxConfigsDeviceList implements OnChanges {
    @Input() profile: ICameraDeviceParameterProfileSettings;
    @Input() accountGroupConfigs: IAccountGroupConfigDto;
    @Input() loading: boolean;
    @Input() timezones: ITimeZoneInfo[];
    peripheralWithConfigRecords: IPeripheralWithConfigRecord[];
    filteredRecords: IPeripheralWithConfigRecord[];

    tableColumns: IVTableColumn<IPeripheralWithConfigRecord>[];
    tableRows: IVTableRow[];

    filterTabs: IFilterBarTab<IPeripheralWithConfigRecord>[];
    filterEvent: IAggregateFilterEvent<IPeripheralWithConfigRecord>;

    selectedRow: IPeripheralWithConfigRecord;

    constructor(
        private readonly filterBarService: FilterBarService,
        private readonly vTableService: VTableService,
    private readonly translationService: TranslationService) {
        this.createTabs();
        this.createTable();
    }


    ngOnChanges(changes: SimpleChanges): void {

        if (changes['accountGroupConfigs']) {

            this.accountGroupConfigs?.PeripheralWithConfigRecord.forEach(r => {
                this.createExceptionPills(r);
            });

            this.peripheralWithConfigRecords = this.accountGroupConfigs?.PeripheralWithConfigRecord;

            this.filteredRecords = this.peripheralWithConfigRecords;

            this.tableRows = this.vTableService.createTableRows(this.peripheralWithConfigRecords, this.tableColumns);
        }
    }

    onFilterChanged($event: any): void {

        if (!$event.trigger) {
            return;
        }

        this.filterEvent = $event;

        this.filteredRecords = this.filterEvent.filteredData;
    }

    rowClicked($event: IVTableRow): void {
        this.selectedRow = $event.data;
    }

    closeSidebar(): void {
        this.selectedRow = null;
    }

    private computeCellStatus(errors: IProfileExceptionErrors, cellErrors: FtCloudConfigExceptionError[]): VTableRowStatus {
        if (!errors || !errors.Errors || !Array.isArray(cellErrors)) {
            return null;
        }

        return cellErrors.some(cellError => errors.Errors.includes(cellError))
            ? VTableRowStatus.Failure
            : VTableRowStatus.Success;
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "StreamaxConfigs.ConfigCollectedDate", sorted: true, sortDirection: SortDirection.Descending, width: 150 }),
                createCellFunction: (camera) => this.vTableService.createDateCell(camera.LatestRecord?.CreatedTime)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "FoundationHardware.Serial",
                    width: 150
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.Peripherals.Serial)
            },
            {
                header: this.vTableService.createVehicleTableHeader(),
                createCellFunction: (camera) => this.vTableService.createVehicleRegCell(camera.Peripherals.vehicleMapping?.vehicle)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "Common.VehicleType",
                    width: 150
                }),
                createCellFunction: (camera) => this.vTableService.createEnumCell(VehicleType, "VehicleType", camera.Peripherals.vehicleMapping?.vehicle?.VehicleType ?? VehicleType.Unknown)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "Common.Product"
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.Peripherals.Product.Description)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.ProtocolVersion",
                    width: 150
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(this.getProtocolVersion(camera))
            },
            // Profile deviations
            {
                header: this.vTableService.createTableHeader({
                    tKey: "Common.General",
                    classes: "centre"
                }),
                createCellFunction: (camera) => this.vTableService.createStatusCell(this.computeCellStatus(camera.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors ?? null, [
                    FtCloudConfigExceptionError.AudioToggle,
                    FtCloudConfigExceptionError.IgnitionDelay,
                    FtCloudConfigExceptionError.RecordOverwrite
                ]))
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.AccAlarm",
                    classes: "centre"
                }),
                createCellFunction: (camera) => this.vTableService.createStatusCell(this.computeCellStatus(camera.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors ?? null, [
                    FtCloudConfigExceptionError.AcceleratorAlarmToggle,
                    FtCloudConfigExceptionError.HardAccelerationTrigger,
                    FtCloudConfigExceptionError.HardAccelerationTriggerMetrics,
                    FtCloudConfigExceptionError.HarshBrakingTrigger,
                    FtCloudConfigExceptionError.HarshBrakingTriggerMetrics,
                    FtCloudConfigExceptionError.HarshLeftTurnTrigger,
                    FtCloudConfigExceptionError.HarshLeftTurnTriggerMetrics,
                    FtCloudConfigExceptionError.HarshRightTurnTrigger,
                    FtCloudConfigExceptionError.HarshRightTurnTriggerMetrics,
                    FtCloudConfigExceptionError.GyroMetrics
                ]))
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.StatusAlarms",
                    classes: "centre"
                }),
                createCellFunction: (camera) => this.vTableService.createStatusCell(this.computeCellStatus(camera.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors ?? null, [
                    FtCloudConfigExceptionError.VideoLossAlarmToggle,
                    FtCloudConfigExceptionError.VideoLossAlarmTrigger,
                    FtCloudConfigExceptionError.CameraCoveredAlarmToggle,
                    FtCloudConfigExceptionError.CameraCoveredAlarmTrigger
                ]))
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "UserPreferences.timezone",
                    classes: "centre"
                }),
                createCellFunction: (camera) => this.vTableService.createStatusCell(this.computeCellStatus(camera.LatestRecord?.CameraDeviceParameterSettings?.ProfileExceptionErrors ?? null, [
                    FtCloudConfigExceptionError.Timezone,
                    FtCloudConfigExceptionError.DaylightSavingsToggle,
                    FtCloudConfigExceptionError.DaylightSavingsOffset
                ]))
            },
            // Config Values
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.AudioVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.ChannelSpecificSettings.some(s => s.AudioEnabled == true && s.CameraConnected) ? "true" : "false")
            },

            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.IgnDelayVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.IgnitionDelaySeconds)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.OverWriteVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createEnumCell(StreamaxRecordOverwrite, "StreamaxRecordOverwrite", camera.LatestRecord?.CameraDeviceParameterSettings?.RecordOverwrite)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.Acc1AlarmVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Acc1AlarmSettings?.AlarmEnabled == null ? "N/A" : camera.LatestRecord.CameraDeviceParameterSettings.Acc1AlarmSettings?.AlarmEnabled ? "Enabled" : "Disabled")
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.Acc2AlarmVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.AlarmEnabled == null ? "N/A" : camera.LatestRecord.CameraDeviceParameterSettings.Accel2AlarmSettings?.AlarmEnabled ? "Enabled" : "Disabled")
            },

            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardBrakeTriggerVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshBrakingTriggerSettings?.TriggerEnabled)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardBrakeOffset",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshBrakingTriggerSettings?.Offset)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardBrakeSpeedStart",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshBrakingTriggerSettings?.StartSpeed)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardBrakeSpeedEnd",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshBrakingTriggerSettings?.EndSpeed)
            },
            //{
            //    header: this.vTableService.createTableHeader({
            //        title: "HarshBraking ReApply seconds",
            //        exportOnly: true
            //    }),
            //    createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord.CameraDeviceParameterSettings.Accel2AlarmSettings?.HarshBrakingTriggerSettings?.ReAlertTimeOffset)
            //},
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardAccTriggerVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HardAccelerationTriggerSettings?.TriggerEnabled)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardAccOffset",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HardAccelerationTriggerSettings?.Offset)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardAccSpeedStart",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HardAccelerationTriggerSettings?.StartSpeed)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardAccSpeedEnd",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HardAccelerationTriggerSettings?.EndSpeed)
            },
            //{
            //    header: this.vTableService.createTableHeader({
            //        title: "HarshAcceleration ReApply seconds",
            //        exportOnly: true
            //    }),
            //    createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HardAccelerationTriggerSettings?.ReAlertTimeOffset)
            //},
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardLeftTriggerVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshLeftTurnTriggerSettings?.TriggerEnabled)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardLeftOffset",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshLeftTurnTriggerSettings?.Offset)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardLeftSpeedStart",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshLeftTurnTriggerSettings?.StartSpeed)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardLeftSpeedEnd",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshLeftTurnTriggerSettings?.EndSpeed)
            },
            //{
            //    header: this.vTableService.createTableHeader({
            //        title: "HarshLeft ReApply seconds",
            //        exportOnly: true
            //    }),
            //    createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshLeftTurnTriggerSettings?.ReAlertTimeOffset)
            //},
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardRightTriggerVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshRightTurnTriggerSettings?.TriggerEnabled)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardRightOffset",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshRightTurnTriggerSettings?.Offset)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardRightSpeedStart",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshRightTurnTriggerSettings?.StartSpeed)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.HardRightSpeedEnd",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshRightTurnTriggerSettings?.EndSpeed)
            },
            //{
            //    header: this.vTableService.createTableHeader({
            //        title: "HarshRight ReApply seconds",
            //        exportOnly: true
            //    }),
            //    createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings?.HarshRightTurnTriggerSettings?.ReAlertTimeOffset)
            //},
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.CamCoveredAlarm",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.CameraCoveredAlarmSettings?.AlarmEnabled ? "Enabled" : "Disabled")
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.CamCoveredAlarmTriggers",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(this.getChannelTriggers(camera.LatestRecord?.CameraDeviceParameterSettings?.CameraCoveredAlarmSettings?.ChannelTriggersEnabled))
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.VideoLossAlarm",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.VideoLossAlarmSettings?.AlarmEnabled)
            },

            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.VideoLossAlarmTriggers",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(this.getChannelTriggers(camera.LatestRecord?.CameraDeviceParameterSettings?.VideoLossAlarmSettings?.ChannelTriggersEnabled))
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.ConnectedChannels",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(this.getConnectedChannelPositions(camera.LatestRecord?.CameraDeviceParameterSettings?.ChannelSpecificSettings ?? []))
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.TimezoneVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.LocalTimeSettings?.TimezoneId)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "StreamaxConfigs.DstVal",
                    exportOnly: true
                }),
                createCellFunction: (camera) => this.vTableService.createTableCell(camera.LatestRecord?.CameraDeviceParameterSettings?.LocalTimeSettings?.DSTEnabled)
            },
            {
                header: this.vTableService.createTableHeader({
                    tKey: "Common.Errors",
                    minWidth: 500
                }),
                createCellFunction: camera => this.vTableService.createTableCell(camera, {
                    component: VPillsComponent,
                    componentInputs: {
                        pills: camera.ExceptionPills
                    },
                    sortData: camera.LatestRecord?.CameraDeviceParameterSettings.ProfileExceptionErrors.Errors.length
                })
            }
        ]
    }

    private getProtocolVersion(record: IPeripheralWithConfigRecord): string {
        if (record.LatestRecord?.CameraDeviceParameterSettings?.Acc1AlarmSettings != null) {
            return "v1"
        }

        if (record.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings != null) {
            return `v2.${record.LatestRecord?.CameraDeviceParameterSettings?.Accel2AlarmSettings.Accel2AlarmType}`
        }

        return "N/A"
    }

    private createExceptionPills(record: IPeripheralWithConfigRecord): void {

        record.ExceptionPills = [];

        if (!record.LatestRecord?.CameraDeviceParameterSettings) {
            record.ExceptionPills.push({ label: FtCloudConfigExceptionError[FtCloudConfigExceptionError.Uncollected], data: FtCloudConfigExceptionError.Uncollected, hovering: false });

            return;
        }

        const errors = record.LatestRecord.CameraDeviceParameterSettings.ProfileExceptionErrors.Errors;

        let tooltip = "";
        let extraCount = 0;

        for (var i = 0; i < errors.length; i++) {
            const labelAndTooltip = this.translationService.tEnum(FtCloudConfigExceptionError, "FtCloudConfigExceptionError", errors[i]);

            if (i < 2) {
                record.ExceptionPills.push({
                    label: labelAndTooltip,
                    hovering: false,
                    data: errors[i]
                });
            }
            else {
                extraCount++
                tooltip += `${labelAndTooltip} `
            }
        }

        if (extraCount > 0) {
            record.ExceptionPills.push({
                label: `+${extraCount}`,
                hovering: true,
                tooltip: tooltip
            })
        }
    }

    private getConnectedChannelPositions(settings: IChannelSpecificSettings[]): number[] {
        if (!settings.length) {
            return;
        }

        return settings
            .filter(setting => setting.CameraConnected)
            .map(setting => setting.ChannelPosition);
    }

    private getChannelTriggers(value: number): string {
        if (!value) {
            return "None";
        }

        const positions: number[] = [];
        let position = 1;

        while (value > 0) {
            if (value & 1) {
                positions.push(position); // Add the position if the bit is set
            }
            value >>= 1;
            position++;
        }

        return positions.length > 0 ? positions.toString() : "None";
    }

    private createTabs(): void {
        this.filterTabs = [];

        this.filterTabs.push(this.filterBarService.createTabDefinition<IPeripheralWithConfigRecord>("StreamaxConfigs.InDate", this.isActivePredicate));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IPeripheralWithConfigRecord>("StreamaxConfigs.Expired", this.IsExpiredPredicate));
        this.filterTabs.push(this.filterBarService.createTabDefinition<IPeripheralWithConfigRecord>("StreamaxConfigs.Uncollected", this.isUncollectedPredicate));
    }

    private IsExpiredPredicate = (record: IPeripheralWithConfigRecord) => {
        if (!record.LatestRecord) {
            return false;
        }

        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

        return new Date(record.LatestRecord.CreatedTime).getTime() < twoWeeksAgo.getTime();
    }

    private isActivePredicate = (record: IPeripheralWithConfigRecord) => {
        if (!record.LatestRecord) {
            return false;
        }

        const twoWeeksAgo = new Date();
        twoWeeksAgo.setDate(twoWeeksAgo.getDate() - 14);

        const now = new Date();
        const recordTime = new Date(record.LatestRecord.CreatedTime).getTime();

        return recordTime >= twoWeeksAgo.getTime() && recordTime <= now.getTime();
    }

    private isUncollectedPredicate = (record: IPeripheralWithConfigRecord) => {
        return record.LatestRecord == null;
    }
}
